<template>
  <header
    class="wc-desktop-header-v2"
    :class="{ 'wc-desktop-header-v2--assoc': isEmployee && isCustomer }"
    role="banner"
    id="desktop-header-v2"
    @click.capture="onHeaderClickCapture"
  >
    <WCEmployeeAssociationBanner v-if="isEmployee && isCustomer" />
    <section class="wc-desktop-header-v2__content container-xl">
      <div class="wc-desktop-header-v2__row">
        <WCHamburgerMenu @showMenu="toggleSideMenu" />

        <div class="wc-desktop-header-v2__brand" v-if="isNotAnEmployee">
          <router-link to="/" v-if="!isCustomerModeScan">
            <WCImage
              :imageSrc="brandImageSrc"
              :imageAlt="getStoreName"
              :imageClass="brandImageClass"
              :defaultImage="false"
              data-testid="desktop-header-v2-brand"
              @imgLoadError="onBrandImageError"
            />
          </router-link>
          <div v-else>
            <WCImage
              :imageSrc="brandImageSrc"
              :imageAlt="getStoreName"
              :imageClass="brandImageClass"
              :defaultImage="false"
              @imgLoadError="onBrandImageError"
            />
          </div>
        </div>
        <div class="wc-desktop-header-v2__brand" v-else>
          <WCImage
            :imageSrc="brandImageSrc"
            :imageAlt="getStoreName"
            :imageClass="brandImageClass"
            :defaultImage="false"
            @imgLoadError="onBrandImageError"
          />
        </div>

        <button
          v-if="isNotAnEmployee"
          class="unstyled-btn wc-desktop-header-v2__store"
          type="button"
          data-testid="desktop-header-v2-store-pill"
          :aria-label="$t('selectStore')"
          @click="goToSelectStore"
        >
          <WCSvgIcon name="location-marker" class="wc-desktop-header-v2__store-icon" />
          <span class="wc-desktop-header-v2__store-label">
            {{ getStoreName }}
          </span>
          <WCSvgIcon name="chevron-down" class="wc-desktop-header-v2__chevron" />
        </button>

        <div class="wc-desktop-header-v2__search">
          <WCSearchBarV2
            v-if="
              $route.name !== giftCardRouteName &&
                isNotAnEmployee &&
                $configuration.itemSearchEnabled
            "
            @search="search"
            @fetchSuggestions="fetchSuggestions"
            :suggestions="suggestions"
            :itemGroups="itemGroups"
          />
        </div>

        <div class="wc-desktop-header-v2__account">
          <WCLoginMenuV2 v-if="isGuest && !isEmployee" />
          <button
            v-else
            class="unstyled-btn wc-desktop-header-v2__profile"
            :class="{ 'wc-desktop-header-v2__profile--assoc': isEmployee && isCustomer }"
            type="button"
            :aria-label="$t('accountSettings')"
            data-testid="desktop-header-v2-profile"
            @click="goToAccountSettings"
          >
            <WCSvgIcon name="user" class="wc-desktop-header-v2__profile-icon" />
            <WCUserGreetingV2 :layout="userGreetingLayout.HEADER" />
          </button>
        </div>

        <button
          v-if="
            isNotAnEmployee &&
              ($configuration.cartEnabled || isCustomerModeScan) &&
              !shouldHideItemPrices
          "
          class="unstyled-btn wc-desktop-header-v2__cart"
          type="button"
          :aria-label="$t('shoppingCartWithItemsCount', { count: cartItemCount })"
          @click="toggleCartDrawer"
          id="desktop-header-v2-cart-button"
        >
          <WCSpinner v-if="isSyncing" color="primary" size="small" />
          <WCSvgIcon
            v-else
            name="shopping-cart"
            class="wc-desktop-header-v2__cart-icon"
            :iconSize="cartItemCount === 0 ? 'xl' : null"
          />
          <span v-if="cartItemCount > 0" class="wc-desktop-header-v2__cart-count">
            {{ cartItemCountLabel }}
          </span>
        </button>
      </div>
    </section>
    <WCDesktopNavBarModern :wcHeaderHeight="wcHeaderHeight" />
  </header>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { markRaw } from 'vue';
import UserMixin from '@/modules/user/mixins/UserMixin';
import EmployeeMixin from '@/modules/employee/mixins/EmployeeMixin';
import CartItemControls from '@/modules/cart/mixins/CartControls/CartItemControls';
import HidePricingMixin from '@/mixins/HidePricingMixin';
import SideMenuService from '@/modules/sidebar/services/SideMenuService';
import emitter from '@/eventBus';
import { SIDE_MENU_EVENTS } from '@/constants/EventConstants';
import WCHamburgerMenu from '@/components/WCHamburgerMenu/WCHamburgerMenu.vue';
import WCImage from '@/components/WCImage/WCImage.vue';
import WCLoginMenuV2 from '@/components/WCLoginMenuV2/WCLoginMenuV2.vue';
import WCSpinner from '@/components/WCSpinner/WCSpinner.vue';
import WCUserGreetingV2 from '@/components/WCUserGreetingV2/WCUserGreetingV2.vue';
import WCEmployeeAssociationBanner from '@/modules/employee/components/WCEmployeeAssociationBanner/WCEmployeeAssociationBanner.vue';
import { GIFT_CARD_ROUTE_NAME, USER_GREETING_LAYOUT } from '@/constants/AppConstants';
import WCSearchBarV2 from '../WCSearchBarV2/WCSearchBarV2.vue';
import WCDesktopNavBarModern from '../WCDesktopNavBarModern/WCDesktopNavBarModern.vue';

export default {
  name: 'WCDesktopHeaderV2',
  mixins: [UserMixin, EmployeeMixin, CartItemControls, HidePricingMixin],
  components: {
    WCHamburgerMenu,
    WCImage,
    WCDesktopNavBarModern,
    WCLoginMenuV2,
    WCSearchBarV2,
    WCSpinner,
    WCUserGreetingV2,
    WCEmployeeAssociationBanner,
  },
  data() {
    return {
      userGreetingLayout: USER_GREETING_LAYOUT,
      brandImageSrc: '/api/img/favicon?w=96&h=96',
      isUsingFaviconImage: true,
      hasTriedStoreImageFallback: false,
      headerResizeObserver: null,
      isSideMenuOpen: false,
      wcHeaderHeight: '0px',
    };
  },
  created() {
    this.brandImageSrc = this.getFaviconSrc;
    this.updateHomeStore();
    this.onSidebarOpen = () => {
      this.setSideMenuOpen(true);
    };
    this.onSidebarClose = () => {
      this.setSideMenuOpen(false);
    };
    this.onSidebarToggle = () => {
      this.setSideMenuOpen(!this.isSideMenuOpen);
    };
    emitter.$on(SIDE_MENU_EVENTS.OPEN, this.onSidebarOpen);
    emitter.$on(SIDE_MENU_EVENTS.CLOSE, this.onSidebarClose);
    emitter.$on(SIDE_MENU_EVENTS.TOGGLE, this.onSidebarToggle);
  },
  mounted() {
    this.updateHeaderHeightCssVar();
    this.observeHeaderHeight();
  },
  unmounted() {
    this.disconnectHeaderHeightObserver();
    document.documentElement.style.setProperty('--mobile-header-height', '0px');
    emitter.$off(SIDE_MENU_EVENTS.OPEN, this.onSidebarOpen);
    emitter.$off(SIDE_MENU_EVENTS.CLOSE, this.onSidebarClose);
    emitter.$off(SIDE_MENU_EVENTS.TOGGLE, this.onSidebarToggle);
    document.body.style.overflow = '';
  },
  computed: {
    ...mapGetters({
      suggestions: 'browse/getSuggestions',
      isSyncing: 'cart/isSyncing',
      carts: 'cart/nonEmptyCarts',
      isCustomerModeScan: 'user/isCustomerModeScan',
    }),
    getFaviconSrc() {
      return '/api/img/favicon?w=96&h=96';
    },
    getStoreImageSrc() {
      return `${this.$configuration.cdn}/api/img/${this.$configuration.store.image}`;
    },
    getStoreName() {
      return this.$configuration.store.name;
    },
    isMultiStore() {
      return this.$configuration.multistore;
    },
    brandImageClass() {
      return this.isUsingFaviconImage
        ? 'logo wc-desktop-header-v2__brand-image wc-desktop-header-v2__brand-image--favicon'
        : 'logo wc-desktop-header-v2__brand-image wc-desktop-header-v2__brand-image--logo';
    },
    giftCardRouteName() {
      return GIFT_CARD_ROUTE_NAME;
    },
    itemGroups() {
      return this.$configuration.mainMenu?.children;
    },
    cartItemCount() {
      if (this.$configuration.orderTypesEnabled && !this.isCustomerModeScan) {
        let itemsCount = 0;
        this.carts.forEach(cart => {
          itemsCount += this.cartLineItemCount(cart);
        });
        return itemsCount;
      }
      return this.cartLineItemCount();
    },
    cartItemCountLabel() {
      return `${this.cartItemCount} ${
        this.cartItemCount === 1 ? this.$t('item') : this.$t('items')
      }`;
    },
  },
  watch: {
    $route() {
      if (this.isSideMenuOpen) {
        this.setSideMenuOpen(false);
      }
    },
  },
  methods: {
    ...mapActions({ fetchSuggestions: 'browse/fetchSuggestions' }),
    toggleSideMenu() {
      SideMenuService.toggle();
    },
    setSideMenuOpen(isOpen) {
      this.isSideMenuOpen = isOpen;
      document.body.style.overflow = isOpen ? 'hidden' : '';
      if (isOpen) {
        emitter.$emit('cartDrawer:close');
      }
    },
    onHeaderClickCapture(event) {
      if (!this.isSideMenuOpen) {
        return;
      }
      event.stopPropagation();
      event.preventDefault();
      SideMenuService.close();
    },
    onBrandImageError() {
      if (!this.hasTriedStoreImageFallback && this.getStoreImageSrc) {
        this.hasTriedStoreImageFallback = true;
        this.isUsingFaviconImage = false;
        this.brandImageSrc = this.getStoreImageSrc;
      }
    },
    updateHomeStore() {
      if (!this.isMultiStore) {
        this.setUserHomeStore(this.$configuration.store.id);
      }
    },
    search({ term, department, itemGroup }) {
      this.$router.push({
        name: 'Browse',
        query: { q: term, g: itemGroup, ...(department ? { Dept: [department] } : undefined) },
      });
    },
    toggleCartDrawer() {
      this.emitter.$emit('cartDrawer:toggle');
    },
    goToAccountSettings() {
      this.$router.push({ name: 'Account Settings' }).catch(() => {});
    },
    goToSelectStore() {
      this.$router.push({ name: 'SelectStore' }).catch(() => {});
    },
    updateHeaderHeightCssVar() {
      const headerHeight =
        this.$el?.getBoundingClientRect?.().height || this.$el?.offsetHeight || 0;
      document.documentElement.style.setProperty('--mobile-header-height', `${headerHeight}px`);
      this.wcHeaderHeight = `${headerHeight}px`;
    },

    observeHeaderHeight() {
      if (typeof ResizeObserver !== 'function' || !this.$el) {
        return;
      }

      this.disconnectHeaderHeightObserver();
      const resizeObserver = new ResizeObserver(() => {
        this.updateHeaderHeightCssVar();
      });

      if (!resizeObserver || typeof resizeObserver.observe !== 'function') {
        return;
      }

      this.headerResizeObserver = markRaw(resizeObserver);
      this.headerResizeObserver.observe(this.$el);
    },

    disconnectHeaderHeightObserver() {
      const resizeObserver = this.headerResizeObserver;
      this.headerResizeObserver = null;

      if (!resizeObserver || typeof resizeObserver.disconnect !== 'function') {
        return;
      }

      resizeObserver.disconnect();
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-desktop-header-v2 {
  position: sticky;
  top: 0;
  z-index: map-get($zindex, 'header');
  background-color: var(--white, $white);
  box-shadow: 0 convert-px-to-rem(1) convert-px-to-rem(4) rgba(0, 0, 0, 0.08);
}

.wc-desktop-header-v2--assoc {
  padding-top: var(--assoc-banner-height, convert-px-to-rem(40));

  :deep(.customer-association-banner) {
    z-index: map-get($zindex, 'header') + 1;
  }
}

.wc-desktop-header-v2__content {
  padding: $pad-2 $pad-3;
}

.wc-desktop-header-v2__row {
  display: flex;
  align-items: center;
  gap: $pad-3;
}

.wc-desktop-header-v2__brand-image {
  display: block;
}

.wc-desktop-header-v2__brand-image--favicon {
  object-fit: cover;
  border: 1px solid var(--gray-300, $gray-300);
  background-color: var(--white, $white);
}

.wc-desktop-header-v2__brand-image--logo {
  width: auto;
  max-width: convert-px-to-rem(160);
  max-height: convert-px-to-rem(42);
  height: auto;
  border-radius: 0;
  border: 0;
  object-fit: contain;
  background-color: transparent;
}

.wc-desktop-header-v2__store {
  flex-shrink: 0;
  border: 0.5px solid var(--dark-lighter-5, get-color('dark', 'lighter-5'));
  border-radius: convert-px-to-rem(999);
  background-color: var(--white, $white);
  min-height: convert-px-to-rem(40);
  padding: $pad-2 $pad-3;
  display: inline-grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: $pad-2;
  text-align: left;
  white-space: nowrap;
}

.wc-desktop-header-v2__store-icon,
.wc-desktop-header-v2__chevron {
  color: var(--primary, $primary);
}

.wc-desktop-header-v2__store-label {
  min-width: 0;
  font-size: $font-size-base;
  line-height: 1.2;
  color: var(--body-color, $body-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wc-desktop-header-v2__search {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0 3rem;
}

.wc-desktop-header-v2__profile {
  appearance: none;
  background: transparent;
  border: 0;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  min-width: 4.25rem;
  padding: 0.375rem 0.75rem;
  color: var(--body-color, $body-color);
  border-radius: 0.375rem;
}

.wc-desktop-header-v2__profile--assoc {
  flex-direction: row;
  gap: 0.5rem;
  background-color: var(--primary-lighter-9, var(--primary));
}

.wc-desktop-header-v2__profile-icon {
  font-size: $font-size-xl;
  color: var(--primary, $primary);
}

.wc-desktop-header-v2__cart {
  appearance: none;
  background: transparent;
  border: 0;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: convert-px-to-rem(60);
  padding: 0;
  color: var(--body-color, $body-color);
}

.wc-desktop-header-v2__cart-icon {
  color: var(--primary, $primary);
  width: convert-px-to-rem(20);
  height: convert-px-to-rem(18);
}

.wc-desktop-header-v2__cart-count {
  margin-top: convert-px-to-rem(2);
  font-size: $font-size-base;
  line-height: 1.2;
  white-space: nowrap;
}
</style>
